import { Language } from "@safetyculture/s12-apis-connect-web/s12/common/languages_pb";
import { PriceTier } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/components_pb";
import { PaymentMethod } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import {
  Currency,
  Period,
  SubscriptionType,
  Tier,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { TimezoneDisplay } from "../types/timezone-display";
import { WORKFLOW_CLIENT_TYPES } from "./workflow-client-types";

export const timezonesToDisplay: TimezoneDisplay = [
  { code: "au", timezone: "Australia/Sydney" },
  { code: "ph", timezone: "Asia/Manila" },
  { code: "nl", timezone: "Europe/Amsterdam" },
  { code: "gb", timezone: "Europe/London" },
  { code: "us", timezone: "America/Chicago" },
];

export const DEFAULT_REGION_CODE = "us";
export const AU_REGION_CODE = "au";
export const DEFAULT_WORKFLOW_CLIENT_TYPE = WORKFLOW_CLIENT_TYPES.GOVERNANCE;

export const periscopeThemeKey = "periscope-theme-prefers-dark";
export const periscopeThemeKeyEvent = periscopeThemeKey + "-event";
export const USER_LIST_PREFERENCE_KEY = "user-list-prefers-admins-only";

export const DEFAULT_THEME_COLOR = "#3f51b5";
export const DEFAULT_AVATAR_SIZE = 45;
export const PERISCOPE_USER_GUIDE_LINK =
  "https://safetyculture.atlassian.net/wiki/spaces/PERISCOPE/pages/2239464025/User+Guide";
export const PERISCOPE_TECHNICAL_FAQ =
  "https://safetyculture.atlassian.net/wiki/spaces/PERISCOPE/pages/1652820710/Technical+FAQ";
export const PERISCOPE_REQUEST_SUDO_ACCESS =
  "https://safetyculture.atlassian.net/wiki/spaces/PERISCOPE/pages/1880556271/FAQ#How-can-I-request-Sudo-access-for-CPR-or-customer-incident%3F";
export const ANNUAL_TO_MONTHLY_GUIDE_LINK =
  "https://safetyculture.atlassian.net/wiki/spaces/PERISCOPE/pages/2478047237/Move+a+Customer+from+Annual+to+Monthly";
export const PERISCOPE_SUDO_GUIDE_LINK =
  "https://safetyculture.atlassian.net/wiki/spaces/PERISCOPE/pages/3298853376/Sudo";
export const SC_APP_URL = "https://app.safetyculture.com";
export const DEFAULT_SNACKBAR_DURATION = 6000;
export const DEFAULT_CLIPBOARD_SB_DURATION = 3500;
export const FREE_TEAM_SEAT_LIMIT = 10;

export enum EventTypeReason {
  Clickaway = "clickaway",
}

export const NO_DISPLAY_DATA = "N/A";
export const DEFAULT_TABLE_PAGINATION = 20;

export const subscriptionTypeNameDisplayMapping = {
  [SubscriptionType.FREE]: "Free Indv.",
  [SubscriptionType.FREE_ORG]: "Free Plan",
  [SubscriptionType.PREMIUM_MONTHLY]: "Paid Plan (monthly)",
  [SubscriptionType.PREMIUM_ANNUAL]: "Paid Plan (annual)",
  [SubscriptionType.PREMIUM_QUARTERLY]: "Paid Plan (quarterly)",
  [SubscriptionType.PREMIUM_BIANNUAL]: "Paid Plan (biannual)",
  [SubscriptionType.PREMIUM_TWO_YEAR]: "Paid Plan (two year)",
  [SubscriptionType.PREMIUM_THREE_YEAR]: "Paid Plan (three year)",
};

export const subscriptionPremiumPeriodNameDisplayMapping = {
  [SubscriptionType.FREE]: "trial",
  [SubscriptionType.FREE_ORG]: "trial",
  [SubscriptionType.PREMIUM_MONTHLY]: "monthly",
  [SubscriptionType.PREMIUM_ANNUAL]: "annual",
  [SubscriptionType.PREMIUM_QUARTERLY]: "quarterly",
  [SubscriptionType.PREMIUM_BIANNUAL]: "biannual",
  [SubscriptionType.PREMIUM_TWO_YEAR]: "2year",
  [SubscriptionType.PREMIUM_THREE_YEAR]: "3year",
};

export const subscriptionPaymentMethodDisplayMapping = {
  [PaymentMethod.AUTOMATIC]: "Automatic",
  [PaymentMethod.INVOICE]: "Invoice",
};

export const pricingPeriodDisplayMapping = {
  [Period.MONTH]: "Monthly",
  [Period.YEAR]: "Yearly",
  [Period.QUARTER]: "Quarterly",
  [Period.HALF_YEAR]: "Half-yearly",
  [Period.TWO_YEAR]: "Two-year",
  [Period.THREE_YEAR]: "Three-year",
};

export const pricingPeriodToMonthsMapping = {
  [Period.MONTH]: 1,
  [Period.YEAR]: 12,
  [Period.QUARTER]: 3,
  [Period.HALF_YEAR]: 6,
  [Period.TWO_YEAR]: 24,
  [Period.THREE_YEAR]: 36,
};

export const priceTierDisplayMapping = {
  [PriceTier.UNSPECIFIED]: "Not specified",
  [PriceTier.PREMIUM]: "Premium",
  [PriceTier.ENTERPRISE]: "Enterprise",
};

export const subscriptionTierDisplayMapping = {
  [Tier.PREMIUM]: "Premium",
  [Tier.ENTERPRISE]: "Enterprise",
};

export enum ChargifyPlanTypeCustomFieldValue {
  PREMIUM = "Premium",
  ENTERPRISE = "Enterprise",
  UNSPECIFIED = "Unspecified",
}

export enum SudoRequestStatus {
  PENDING = "PENDING",
  USED = "USED",
  EXPIRED = "EXPIRED",
}

export const languageDisplayMapping = {
  [Language.UNSPECIFIED]: "N/A",
  [Language.EN_US]: "en-US",
  [Language.EN_GB]: "en-GB",
  [Language.DE_DE]: "de-DE",
  [Language.ES_ES]: "es-ES",
  [Language.FR_FR]: "fr-FR",
  [Language.PT_BR]: "pt-BR",
  [Language.NL_NL]: "nl-NL",
};

export enum DocPrefix {
  PrefixAudit = "audit",
  PrefixTemplate = "template",
  PrefixAction = "action",
  PrefixUser = "user",
  PrefixNotification = "ntfmsg",
  PrefixEvidence = "evidence",
  PrefixRole = "role",
  PrefixLocation = "location",
  PrefixResponseSet = "responseset",
  PrefixResponse = "response",
  PrefixPreference = "preference",
  PrefixHeadsUp = "heads_up",
  PrefixSubscription = "subscription",
  PrefixSite = "pathsite",
}

export const currencyDisplayMapping = {
  [Currency.AUD]: "AUD",
  [Currency.USD]: "USD",
  [Currency.EUR]: "EUR",
  [Currency.GBP]: "GBP",
};

export const currencySymbolMapping = {
  [Currency.AUD]: "$",
  [Currency.USD]: "$",
  [Currency.EUR]: "€",
  [Currency.GBP]: "£",
};
export enum PillStatusMode {
  EDIT,
  CREATE,
  CHECK,
}

interface SafetyCultureOrgIDMap {
  [region: string]: string;
}

export const SafetyCultureOrgMap: SafetyCultureOrgIDMap = {
  au: "role_9cc5af8eff2b475c959fd99828b672e4",
  eu: "role_27939851f090424185306064949f4c37",
  us: "role_e07b6ac08a0511e29951ddd1182f65d9",
};

export const scvLink =
  "https://prod-useast-b.online.tableau.com/#/site/safetyculture/views/SingleCustomerView3WIP/OrganisationOverview?Entity=";

export enum LoginType {
  UNSPECIFIED = "N/A",
  PASSWORD = "Password only",
  SSO = "SSO only",
  ANY = "Password + SSO",
  PASSWORD_AND_SSO_ENFORCED_FOR_DOMAINS = "Password + SSO enforced for domains",
}

export enum DocumentState {
  UNSPECIFIED = "N/A",
  ACTIVE = "Active",
  ARCHIVED = "Archived",
  DELETED = "Deleted",
}

export const currencyMapping = {
  ["AUD"]: Currency.AUD,
  ["USD"]: Currency.USD,
  ["EUR"]: Currency.EUR,
  ["GBP"]: Currency.GBP,
};
